import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import 'vuetify/dist/vuetify.min.css'
import * as Sentry from '@sentry/vue'

import { pinia } from '@/modules/pinia'
import { useSmartSocietyAuthStore } from '@/stores/smartSocietyAuthStore'

const app = createApp(App).use(vuetify).use(pinia).use(router)

const authStore = useSmartSocietyAuthStore()

const mode = import.meta.env.MODE
if (mode !== 'local-development') {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracePropagationTargets: ['*'],
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        triggerLabel: 'Feedback',
        formTitle: 'Send Feedback',
        submitButtonLabel: 'Send Feedback',
        cancelButtonLabel: 'Cancel',
        messagePlaceholder: 'What would you like to tell us?',
        successMessageText: 'Thank you for your feedback!',
        colorScheme: 'light',
        showName: true,
        showEmail: true,
        enableScreenshot: true,
        autoInject: false
      })
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0
  })
}

function setupSentryFeedbackWidget() {
  if (mode === 'local-development') {
    return
  }

  const feedback = Sentry.getFeedback()

  if (!feedback) {
    return
  }

  let widget: ReturnType<typeof feedback.createWidget> | null = null

  const getSentryUser = () => {
    const { user } = authStore

    if (!user) {
      return null
    }

    const sentryUser: { id?: string; username?: string; email?: string } = {}
    const username = user.name ?? user.given_name

    if (user.sub) {
      sentryUser.id = user.sub
    }

    if (username) {
      sentryUser.username = username
    }

    if (user.email) {
      sentryUser.email = user.email
    }

    return sentryUser
  }

  const removeFeedbackWidget = () => {
    if (widget) {
      try {
        widget.removeFromDom()
      } catch (error) {
        Sentry.captureException(error)
      } finally {
        widget = null
      }
    }
  }

  const createFeedbackWidget = () => {
    const nextWidget = feedback.createWidget()

    try {
      nextWidget.appendToDom()
      widget = nextWidget
    } catch (error) {
      widget = null
      Sentry.captureException(error)
    }
  }

  const updateFeedbackWidget = () => {
    const sentryUser = getSentryUser()

    Sentry.setUser(sentryUser)

    if (!sentryUser) {
      removeFeedbackWidget()
      return
    }

    if (!widget) {
      createFeedbackWidget()
    }
  }

  updateFeedbackWidget()
  authStore.$subscribe(updateFeedbackWidget)
}

authStore.initialize().finally(() => {
  router.isReady().then(() => {
    setupSentryFeedbackWidget()

    const loadingScreen = document.getElementById('loading-screen')
    if (loadingScreen) {
      loadingScreen.classList.add('hidden')
    }

    app.mount('#app')
  })
})
